import { useState } from "react";
import { Link } from "react-router-dom";
import { usePerformanceFilters } from "@/hooks/usePerformanceFilters";
import { useRacePerformance } from "@/hooks/useRacePerformance";
import { computeSummary, filterRaces } from "@/utils/performance/metrics";
import { periodRangeFor, resolvePeriodRange } from "@/utils/performance/period";
import PerformanceFilterBar from "@/components/performance/PerformanceFilterBar";
import FullWidthToggle from "@/components/performance/FullWidthToggle";
import SummaryCards from "@/components/performance/SummaryCards";
import SummaryPanels from "@/components/performance/SummaryPanels";
import TrackLeaderboard from "@/components/performance/TrackLeaderboard";

/**
 * Lexi Performance — Summary dashboard.
 * Internal, desktop-optimised. Built from the approved mockup
 * (docs/mockups/lexi-performance-summary.html) against mock data for now.
 */
// The persisted store starts here; used to clamp the period stepper.
const STORE_FLOOR = "2025-12-01";

export default function PerformanceSummary() {
  const { filters, setFilter, buildDetailUrl } = usePerformanceFilters();
  const [isFullWidth, setIsFullWidth] = useState(false);

  // Fetch only the selected period's slice (lean), computed up front from the
  // anchor (default: today) — so the stepper can range back over the whole
  // store, not just a fixed recent window.
  const todayIso = new Date().toISOString().slice(0, 10);
  const fetchRange = periodRangeFor(
    filters.period,
    filters.anchor || todayIso,
    {
      from: filters.from,
      to: filters.to,
    },
  );
  const { data: races = [], isLoading } = useRacePerformance(
    fetchRange.from && fetchRange.to
      ? { from: fetchRange.from, to: fetchRange.to }
      : {},
  );

  // Full track list for the dropdown — a broad lean fetch (shared cache).
  const { data: recent = [] } = useRacePerformance({ mode: "summary" });
  const tracks = [...new Set(recent.map((r) => r.track))].sort();

  const range = resolvePeriodRange(filters.period, races, {
    anchor: filters.anchor,
    from: filters.from,
    to: filters.to,
  });
  // Stepper ranges across the whole store, not just the loaded slice.
  const dataMin = STORE_FLOOR;
  const dataMax = todayIso;
  const filtered = filterRaces(races, {
    ...filters,
    from: range.from,
    to: range.to,
  });
  const summary = computeSummary(filtered, filters);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800">
      <div
        className={`${isFullWidth ? "w-full" : "max-w-[1240px]"} mx-auto p-6`}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Lexi Performance{" "}
              <span className="text-slate-400 font-normal">/ Summary</span>
            </h1>
            <p className="text-sm text-slate-500">
              Internal · desktop-optimised · live data
            </p>
          </div>
          <div className="flex items-center gap-3">
            <FullWidthToggle
              isFullWidth={isFullWidth}
              onToggle={() => setIsFullWidth((prev) => !prev)}
            />
            <div className="text-xs text-slate-400">/performance</div>
          </div>
        </div>

        <PerformanceFilterBar
          filters={filters}
          setFilter={setFilter}
          tracks={tracks}
          rangeLabel={range.label}
          dataMin={dataMin}
          dataMax={dataMax}
          rightSlot={
            <Link
              to={buildDetailUrl()}
              className="text-sm font-semibold text-white bg-slate-800 hover:bg-slate-700 rounded-lg px-3 py-2"
            >
              View all races →
            </Link>
          }
        />

        {isLoading ? (
          <div className="text-center text-slate-400 py-20">Loading…</div>
        ) : (
          <>
            <SummaryCards summary={summary} type={filters.type} />
            <SummaryPanels summary={summary} />
            <TrackLeaderboard
              leaderboard={summary.leaderboard}
              buildDetailUrl={buildDetailUrl}
            />
          </>
        )}

        <p className="text-center text-xs text-slate-400">
          Live data (full-field results joined to our archived tips). Filters
          carry through to the detail page. See{" "}
          <code>docs/LEXI-PERFORMANCE-REPORTS-SPEC.md</code>.
        </p>
      </div>
    </div>
  );
}
